// The CLIENT-SIDE typed projection shapes the Work surfaces render (Today / Tasks / KPIs / Scheduler /
// Approvals). These MIRROR the typed JSON the Stage-1 HTTP read endpoints return (app/modules/dashboard/api
// read-projections.ts) — the browser client cannot import the backend module types (it is a browser-only
// bundle, tsconfig `types: ["react","react-dom"]`), so the projection contract is restated here as the
// wire-shape the component renders. It carries NO internal/build/third-party identifier (G-197) and NO
// approval-setter (the client never writes a Gate — P-GATE); every displayed state DERIVES from the durable
// lifecycle the owning module returned (G-182), never a client-authored status.
//
// MIRRORED_CONTRACT: Work-surface wire owners live in
// app/modules/dashboard/api/work-wire-projections.ts and app/modules/dashboard/api/read-projections.ts.
// dashboard-work-wire.contract.test.ts enforces exact server/client equality; this file is not a permissive
// subset of richer owning-module views. The client never invents a field the projection does not carry
// (P-SELF — no fabricated data).

import type { TaskClosureKindView } from './know-projections.js';
import type { ScheduleOccurrence, ScheduleProvenanceFields } from './schedule-row-projections.ts';

/** The Task lifecycle states (`04` §2) — the board columns are EXACTLY this set (G-182), never UI-authored. */
export type TaskState = 'draft' | 'briefed' | 'working' | 'blocked' | 'done' | 'dropped';

/** The non-terminal Task states — the "nothing forgotten" set the board never lets stall (T-THESIS-4). */
export type NonTerminalTaskState = 'draft' | 'briefed' | 'working' | 'blocked';

/** One board-projection Task row (by state / owner / metric-link) — the manager-visible per-Task view. */
export interface BoardTaskRow {
  readonly id: string;
  readonly title: string;
  /** The Agent-prepared brief (the card blurb); null only for unenriched legacy Tasks. */
  readonly brief: string | null;
  /** Concise manager-facing purpose: what the Task is for and why it matters. */
  readonly managerSummary: string;
  /** The current structured-plan roll-up; the full definition remains on Task detail. */
  readonly planProgress: {
    readonly total: number;
    readonly completed: number;
    readonly blocked: number;
    readonly currentStep: string | null;
  };
  /** The latest sanitised Task update, or an empty string when no update has been recorded yet. */
  readonly latestUpdate: string;
  /** Which human Gate is currently awaited; null once no approval is pending. */
  readonly waitingOn: 'draft_reviewer' | 'owner' | null;
  /** The owning Task's last durable update time. */
  readonly updatedAt: string;
  readonly state: TaskState;
  /** The Tasks-owned terminal disposition — `state` alone cannot tell a completion from an unresolved stop. */
  readonly closureKind: TaskClosureKindView | null;
  /** Identity-owned display label for grouping; null when the owner cannot be resolved. */
  readonly ownerDisplayName: string | null;
  readonly ownerUserId: string;
  /** Metrics-owned KPI label for grouping; null when unlinked or the linked Metric is unavailable. */
  readonly metricName: string | null;
  /** The linked Metric id, or null for non-metric work (which then carries a category — G-099). */
  readonly metricId: string | null;
  /** The non-metric category when `metricId` is null (never both set). */
  readonly nonMetricCategory: 'hygiene' | 'compliance' | 'risk_control' | 'general' | null;
  /** WHY it is parked (only for a `blocked` Task; null otherwise — G-089). */
  readonly parkedReason: string | null;
  /** WHO is awaited (only for a `blocked` Task; the single named party); null otherwise. */
  readonly awaitedParty: string | null;
  /** The NEXT CHECK time (the `blocked` Task's deterministic wake); null otherwise. */
  readonly nextCheckAt: string | null;
}

/** The selected Today open-Task summary (wake internals remain server-side in Tasks). */
// MIRRORED_CONTRACT: `TodayOpenTaskView` in dashboard/api/work-wire-projections.ts.
export interface NonTerminalTaskRow {
  readonly id: string;
  readonly title: string;
  readonly state: NonTerminalTaskState;
  readonly ownerUserId: string;
  readonly parkedReason: string | null;
}

/** The parked/blocked view row — reason + awaited party + next check, WHOLE-Task granularity (no lanes). */
export interface ParkedTaskRow {
  readonly id: string;
  readonly title: string;
  readonly ownerUserId: string;
  readonly parkedReason: string;
  readonly awaitedParty: string | null;
  readonly nextCheckAt: string | null;
}

/** The Task board projection — per-state counts + the parked set + the open-work cap/headroom. */
export interface TaskBoardProjection {
  /** Count per lifecycle state — the kanban columns are exactly this key set (G-182). */
  readonly countsByState: Readonly<Record<TaskState, number>>;
  /** The current filtered slice (by state/owner/metric-link) — an empty filter returns every Task. */
  readonly filtered: readonly BoardTaskRow[];
  /** The parked/blocked view — reason + awaited party + next check, whole-Task. */
  readonly parked: readonly ParkedTaskRow[];
  /** The open-work count (open agent-created Tasks). */
  readonly openCount: number;
  /** The open-work cap, or null when the Operator set none (no hidden default — G-187). */
  readonly cap: number | null;
  /** Remaining headroom under the cap, or null when no cap is set. */
  readonly headroom: number | null;
}

/**
 * The WIRE shape the server's `composeTaskBoardView` sends (IFC-DASH-001): the tasks-module board stats
 * nested under `board`, with the filtered slice + parked view as siblings. `TasksSurface` FLATTENS this to
 * `TaskBoardProjection` for the board/stat components. Reconciled to the server contract — BUILD-DEC-091.
 */
export interface TaskBoardWire {
  readonly board: {
    readonly countsByState: Readonly<Record<TaskState, number>>;
    readonly openCount: number;
    readonly cap: number | null;
    readonly headroom: number | null;
  };
  readonly filtered: readonly BoardTaskRow[];
  readonly parked: readonly ParkedTaskRow[];
}

/** The direction of a Metric's latest authoritative Value vs its target / recent trend (direction-only). */
export type MetricDirection = 'up' | 'down' | 'flat';

/** The Metric lifecycle state (`04` §4) — proposed awaits activation; active is live; archived is retired. */
export type MetricState = 'proposed' | 'active' | 'archived';

/** The Metric Value lifecycle state (`04` §4). */
export type MetricValueState = 'proposed' | 'authoritative' | 'superseded';

/** The latest authoritative Value shown on the KPI card (value + period + its Value id for evidence). */
export interface MetricLatestValue {
  readonly id: string;
  readonly value: number;
  readonly state: MetricValueState;
  /** The RECORDED period the value covers (ISO dates) — components format, never re-derive. */
  readonly periodStart: string;
  readonly periodEnd: string;
}

/** One RECORDED authoritative point in a Metric's series (real values only — the chart's data). */
export interface MetricPoint {
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly value: number;
}

/** One Task LINKED to a Metric — the "Tasks moving this metric" row + its chart marker. */
export interface MetricLinkedTask {
  readonly id: string;
  readonly title: string;
  /** The Agent-prepared brief (the row's description line); null while still draft. */
  readonly brief: string | null;
  readonly state: TaskState;
  readonly updatedAt: string;
}

/** One KPI (Metric) state card — definition, recorded series, target, trend, linked Tasks. */
export interface MetricCard {
  readonly id: string;
  readonly name: string;
  readonly state: MetricState;
  /** The human definition statement (agent-authored; read-only on this surface). */
  readonly definition: string;
  /** Exact questions the agent recorded when it could not safely finalise the definition. */
  readonly clarificationQuestions: readonly string[];
  readonly latestValue: MetricLatestValue | null;
  /** EVERY recorded authoritative value, oldest → newest — the sparkline/chart series. */
  readonly series: readonly MetricPoint[];
  /** The human-set target (safe parameter), or null — no hidden default. */
  readonly target: number | null;
  /** The cadence label when the recorded cadence carries one (e.g. "weekly"); display only. */
  readonly cadenceLabel: string | null;
  /** Direction of the latest authoritative Value vs the human target (direction-only, G-106). */
  readonly trendDirection: MetricDirection;
  /** Direction over the last N authoritative periods (trend reasoning). */
  readonly recentTrendDirection: MetricDirection;
  readonly linkedTasks: readonly MetricLinkedTask[];
  /** Present only while proposed; the exact server/domain activation-precondition verdict. */
  readonly activationReadiness: MetricActivationReadiness | null;
}

/** MIRRORED_CONTRACT: `MetricActivationReadiness` in app/modules/metrics/metric-lifecycle-service.ts. */
export type MetricActivationReadiness =
  | { readonly ready: true }
  | {
      readonly ready: false;
      readonly reason:
        | 'not_proposed'
        | 'proposal_rejected'
        | 'changes_requested'
        | 'definition_needs_clarification'
        | 'collector_missing'
        | 'collector_not_validated';
    };

/** The KPIs board projection (labelled "KPIs"; the entity is Metric — N-BOARD-2 AC-5). */
export interface MetricsBoardProjection {
  readonly metrics: readonly MetricCard[];
}

/**
 * The selected WIRE shape `composeMetricsBoardView` sends. The server explicitly projects this subset from
 * Metrics and Tasks; full durable Metric/Value/Task objects never cross the browser boundary. The KPIs board
 * and Today share this one shape, then `KpisSurface` flattens it into its component-only `MetricCard` view.
 */
/** MIRRORED_CONTRACT: `MetricStateBrowserView` in dashboard/api/work-wire-projections.ts. */
export interface MetricStateWire {
  readonly metric: {
    readonly id: string;
    readonly name: string;
    readonly state: MetricState;
    readonly definition: {
      readonly statement: string;
      readonly clarificationQuestions?: readonly string[];
    };
    /** Arbitrary recorded cadence (no fixed menu, G-188) — the card renders `kind` when it is a string. */
    readonly cadence: Readonly<Record<string, unknown>>;
    /** The human-editable safe parameters; `target` drives the chart's dashed line. */
    readonly safeParameters: { readonly target: number | null };
  };
  readonly latestAuthoritativeValue: {
    readonly id: string;
    readonly value: number;
    readonly state: MetricValueState;
    readonly period: { readonly start: string; readonly end: string };
  } | null;
  /** Every authoritative Value oldest → newest (MetricStateView.authoritativeHistory). */
  readonly authoritativeHistory: readonly {
    readonly value: number;
    readonly period: { readonly start: string; readonly end: string };
  }[];
  readonly trendDirection: MetricDirection;
  readonly recentTrendDirection: MetricDirection;
  /** The Tasks LINKED to this metric (composed via the tasks port — titles, never bare ids, G-197). */
  readonly linkedTasks: readonly {
    readonly id: string;
    readonly title: string;
    readonly brief: string | null;
    readonly state: TaskState;
    readonly updatedAt: string;
  }[];
  /** Dashboard compose adds the owner-defined verdict for proposed Metrics; null for active Metrics. */
  readonly activationReadiness: MetricActivationReadiness | null;
}

export interface MetricsBoardWire {
  readonly metrics: readonly MetricStateWire[];
}

/** A Metric Value's stored evidence sheet — viewable on demand so a human audits the number (G-011). */
export interface MetricEvidenceSheet {
  readonly method: string;
  readonly sources: readonly string[];
  readonly calculationNarrative: string;
  readonly confidence: 'low' | 'medium' | 'high';
  readonly limitations: string;
  readonly inclusionExclusion: string;
  readonly truncated: boolean;
}

/** The health status of a Schedule / Background Job from its recent runs (single graded marker). */
export type ScheduleHealth = 'healthy' | 'failing' | 'interrupted' | 'running' | 'idle';

/** A single Schedule Run Record state (`04` §3) — failed/interrupted VISIBLY DISTINCT from completed. */
export type RunState = 'running' | 'completed' | 'failed' | 'interrupted';

/** One Run Record row — its state + a sanitised outcome marker (never a body/recipient — G-058). */
// MIRRORED_CONTRACT: `ScheduleRunBrowserView` in dashboard/api/work-wire-projections.ts.
export interface RunRecordRow {
  readonly id: string;
  readonly state: RunState;
  readonly startedAt: string;
  readonly endedAt: string | null;
  /** Exact sanitised Scheduler-owned fields; the browser chooses the first present display marker. */
  readonly outcome: string | null;
  readonly failureClass: string | null;
  readonly error: string | null;
}

/** Every terminal state tallied separately (failed vs interrupted vs completed — G-107/G-111). */
export interface RunTally {
  readonly completed: number;
  readonly failed: number;
  readonly interrupted: number;
  readonly running: number;
}

/** Common Schedule / Background Job health fields from scheduler's owned wire projection. */
export interface ScheduleHealthRowBase {
  readonly name: string;
  readonly health: ScheduleHealth;
  /** The next projected occurrence(s), resolved in the tenant timezone; empty when none is projected.
   *  (Reconciled to the server's health-service row — BUILD-DEC-091.) */
  readonly nextOccurrences: readonly ScheduleOccurrence[];
  readonly recentRuns: readonly RunRecordRow[];
  /** Failure / interruption counts over the recent window (server-provided per row — G-107/G-112). */
  readonly recentFailureCount: number;
  readonly recentInterruptedCount: number;
  readonly outcomeTally: RunTally;
}

/** Browser mirror of Scheduler's closed machine-success vocabulary (exact equality is contract-tested). */
export type ScheduleOutputKind =
  | 'task'
  | 'schedule'
  | 'metric'
  | 'artifact'
  | 'file'
  | 'message'
  | 'integration_proposal';

/**
 * MIRRORED_CONTRACT: ScheduleOutputRequirement -> app/modules/scheduler/types.ts
 * V1 proves one producer per kind; quantitative capacity is not modeled.
 */
export type ScheduleOutputRequirement = Readonly<{ kind: ScheduleOutputKind; minCount: 1 }>;

export interface ScheduleOutputContract {
  readonly version: 1;
  readonly requirements: readonly ScheduleOutputRequirement[];
}

/** A durable human-authored Schedule row uses `scheduleId` on the server, plus its lifecycle state. */
export interface ScheduleHealthScheduleRow extends ScheduleHealthRowBase, ScheduleProvenanceFields {
  readonly scheduleId: string;
  readonly state: 'draft' | 'active' | 'paused' | 'finished' | 'archived';
  /** The RECORDED execution brief the routine was created with — what the detail panel says it DOES. Only
   *  a Schedule carries one; a Background Job's brief is synthesised per run and never persisted. */
  readonly brief: string;
  /** Human-authored narrative output note; it is not a completion criterion. */
  readonly expectedOutput: string;
  /** Exact machine completion requirements; null only for readable pre-contract legacy drafts. */
  readonly outputContract: ScheduleOutputContract | null;
}

/** A config-owned Background Job row uses `id` and `kind` on the server. */
export interface BackgroundJobHealthRow extends ScheduleHealthRowBase {
  readonly id: string;
  readonly kind: string;
}

export type ScheduleHealthRow = ScheduleHealthScheduleRow | BackgroundJobHealthRow;

/** The schedule-health projection — every Schedule plus the registered Background Jobs (G-112). */
// MIRRORED_CONTRACT: `ScheduleHealthBrowserView` in dashboard/api/work-wire-projections.ts.
export interface ScheduleHealthProjection {
  readonly schedules: readonly ScheduleHealthScheduleRow[];
  readonly backgroundJobs: readonly BackgroundJobHealthRow[];
}

/** One "What Ritona did" activity-rail row — the selected, sanitised Audit projection (G-058). */
// MIRRORED_CONTRACT: `ActivityRailRow` in dashboard/api/work-wire-projections.ts.
export interface ActivityRow {
  readonly id: string;
  /** The already-sanitised/hashed actor token (never a raw identity — G-058). */
  readonly actor: string;
  /** The capability exercised — a sanitised operational label, no internal construction (G-158). */
  readonly capability: string;
  /** The object touched (a display ref), or null. */
  readonly objectRef: string | null;
  readonly outcome: string;
  readonly timestamp: string;
}

/** The kind of Gate an Approvals item exercises — the owning module the exercise routes to. */
export type ApprovalGateKind =
  | 'task_draft'
  | 'task_start'
  | 'metric_activation'
  | 'schedule_activation'
  | 'metric_value_authoritative'
  | 'hubspot_write';

/** MIRRORED_CONTRACT: `ApprovalReadiness` in dashboard/api/read-projections.ts. */
export type ApprovalReadiness =
  | { readonly ready: true }
  | {
      readonly ready: false;
      readonly reason:
        | 'task_draft_incomplete'
        | 'metric_not_proposed'
        | 'definition_needs_clarification'
        | 'collector_missing'
        | 'collector_not_validated'
        | 'metric_proposal_rejected'
        | 'metric_changes_requested'
        | 'phase_not_allowed'
        | 'draft_rejected'
        | 'draft_changes_requested'
        | 'missing_output_contract'
        | 'no_future_occurrence'
        | 'global_tools_disabled'
        | 'server_wiring_missing'
        | 'producer_absent'
        | 'producer_dark'
        | 'config_unreadable'
        | 'config_stale'
        | 'config_malformed'
        | 'readiness_check_failed'
        | 'readiness_response_invalid';
      readonly note: string;
    };

/** One pending Approval the authenticated human is authorised for — a composed, module-owned item. */
export interface ApprovalItem {
  readonly kind: ApprovalGateKind;
  /** The durable object the Gate is bound to (Task id / Metric id / Metric Value id). */
  readonly objectId: string;
  /** Exact definition revision or immutable Value version reviewed by a revision-bound Gate. */
  readonly reviewRevision: number | null;
  /** A human-facing label DERIVED from the owning projection — no internal identifier (G-197). */
  readonly title: string;
  /** A short human reason the Gate is pending (what it unlocks) — display only. */
  readonly why: string;
  /**
   * True when the authenticated human is the BOUND actor for this Gate (task-start is bound-owner-only —
   * N-APPROVE-1 AC-4). The dashboard offers the exercise control only when bound; the OWNING module is the
   * real boundary (G-180) — this flag drives rendering, not authorisation.
   */
  readonly boundToMe: boolean;
  /**
   * True when this Gate kind has a REAL owning-module decline leg (BUILD-DEC-116) — server-derived from
   * the one per-kind availability map its decline routes answer from. The surface offers the Decline
   * affordance only when true; a kind without a leg renders an honest line instead (never a dead control).
   */
  readonly declinable: boolean;
  /** A blocked precondition stays visible but cannot be routed as an activation. */
  readonly readiness: ApprovalReadiness;
}

/** The Approvals-inbox projection — the aggregated pending Gates across modules (N-APPROVE-1). */
export interface ApprovalsInboxProjection {
  readonly items: readonly ApprovalItem[];
}

// --- GLOBAL CHAT (N-CHAT-1..4) — the thin-transport projection the chat dock renders ---------------------
//
// The dashboard is a THIN TRANSPORT (N-CHAT-1 AC-2): a send routes to `agent.spawnAgentRun` carrying the
// authenticated human as the initiating Actor; the transcript renders EXACTLY `agent.getRunTranscript`
// (which already excludes system-injected Context and internal mechanics — N-CHAT-3 AC-1); a human can
// interrupt a running run (N-CHAT-2). These client shapes mirror the wire JSON the Stage-1 chat endpoints
// return — the dashboard adds NOTHING to the transcript and authors NO turn (P-SELF): it renders the turns
// the owning module resolved and the run state it recorded (G-182), never a fabricated or optimistic turn.

/** The run lifecycle states (`04` §8) — the dock surfaces the state the `agent` module recorded, never one it
 *  set itself. Only a `running` run offers the human interrupt control (N-CHAT-2). */
export type ChatRunState = 'running' | 'completed' | 'failed' | 'interrupted';

/**
 * Who authored a transcript turn, AS THE SERVER ATTRIBUTED IT TO THIS READER (MIRRORED_CONTRACT:
 * `SurfacedTurnAuthor` in app/modules/agent/types.ts).
 *   • `human`    — the READER's own words. The renderer puts these in the right-aligned "me" bubble, so
 *                  this value must never carry anyone else's turn;
 *   • `teammate` — a human turn on a run the reader did NOT initiate. Reachable since a Schedule's
 *                  conversation became readable by every authenticated human (owner ruling 2026-08-02):
 *                  a colleague's confirmed reply can run on the routine's Session. Same text, attributed
 *                  to someone else — the point of the ruling was to see who said what;
 *   • `agent`    — Ritona's answer;
 *   • `platform` — a visible system-authored Task wake, impersonating neither.
 */
export type ChatTurnAuthor = 'human' | 'agent' | 'platform' | 'teammate';

/**
 * Content-free owning-module reference attached to a persisted turn. Comms owns the full/reviewable Message;
 * the turn text may still contain the bounded reduced input that Agent actually reasoned over.
 */
export interface ChatTurnSourceRef {
  readonly type: 'comms_message';
  readonly messageId: string;
}

/** One turn in the rendered transcript — author + the sanitised text `agent.getRunTranscript` resolved.
 *  Carries no substrate/provider/raw-identity identifier; the sole exception is the closed, actor-scoped
 *  Comms Message reference used to resolve an authorised timeline email (G-197). */
export interface ChatTranscriptTurn {
  readonly id: string;
  readonly author: ChatTurnAuthor;
  readonly text: string;
  /** Optional structured source pointer. The Message reader resolves and scopes all content on demand. */
  readonly sourceRef?: ChatTurnSourceRef;
  /** Server-derived only: a pre-source-ref initiating turn from an inbound Message run. */
  readonly legacyInboundMessage?: true;
}

/**
 * The CLOSED sanitised activity vocabulary (BUILD-DEC-098 — the owner-approved N-CHAT-3 amendment),
 * mirroring the owning module's enum. Fence-honest names (proposed/drafted/requested where a human Gate
 * stands between); a kind is what the PLATFORM recorded, never a raw tool/capability identifier (G-197).
 */
// MIRRORED_CONTRACT: `RUN_ACTIVITY_KINDS` in
// app/modules/agent/types.ts. The browser cannot import the Node-owned module at runtime.
export const CHAT_ACTIVITY_KINDS = [
  'memory_read',
  'memory_write',
  'govdoc_read',
  'file_read',
  'file_write',
  'helper_script',
  'api_exploration',
  'task_proposed',
  'task_briefed',
  'kpi_proposed',
  'kpi_collector_registered',
  'kpi_collector_trialled',
  'schedule_drafted',
  'hubspot_write_drafted',
  'plan_edited',
  'thinking_summary',
  'email_send_requested',
  'email_sent',
] as const;

export type ChatActivityKind = (typeof CHAT_ACTIVITY_KINDS)[number];

/** Runtime guard for the closed activity vocabulary at the untrusted SSE boundary. */
export function isChatActivityKind(value: unknown): value is ChatActivityKind {
  return typeof value === 'string' && (CHAT_ACTIVITY_KINDS as readonly string[]).includes(value);
}

export const CHAT_ACTIVITY_OUTCOMES = ['ok', 'error', 'gate_refused'] as const;
export type ChatActivityOutcome = (typeof CHAT_ACTIVITY_OUTCOMES)[number];

export function isChatActivityOutcome(value: unknown): value is ChatActivityOutcome {
  return typeof value === 'string' && (CHAT_ACTIVITY_OUTCOMES as readonly string[]).includes(value);
}

// MIRRORED_CONTRACT: `RUN_REASONING_TEXT` / `RunReasoningText` in
// app/modules/agent/run-output-broker.ts. These are fixed operational statuses, never model reasoning.
export const CHAT_REASONING_TEXT = Object.freeze({
  THINKING: 'Thinking through your request',
  REVIEWING_CONTEXT: 'Reviewing relevant context',
  FINISHING_RESPONSE: 'Finishing your response',
} as const);

export type ChatReasoningText = (typeof CHAT_REASONING_TEXT)[keyof typeof CHAT_REASONING_TEXT];

/** Fail closed: an arbitrary server/model string is not an operational reasoning status. */
export function isChatReasoningText(value: unknown): value is ChatReasoningText {
  return (
    typeof value === 'string' &&
    (Object.values(CHAT_REASONING_TEXT) as readonly string[]).includes(value)
  );
}

/** One durable sanitised activity row (what the agent DID — BUILD-DEC-098). */
export interface ChatActivityRow {
  readonly id: string;
  readonly kind: ChatActivityKind;
  readonly label: string;
  readonly objectRef?: Readonly<Record<string, unknown>>;
  readonly outcome: ChatActivityOutcome;
  readonly sequence: number;
}

/** The durable, server-authored presentation order for one resolved run. It records only surfaced answer
 *  text and sanitised activity rows; ephemeral operational statuses are deliberately not persisted. */
export type ChatTranscriptPart =
  | { readonly type: 'text'; readonly text: string }
  | { readonly type: 'activity'; readonly activity: ChatActivityRow };

/**
 * Ephemeral live rendering shape. It widens the durable row only in client view-state: a catalog-owned
 * `running` start may omit durable sequence, and settled SSE echoes may retain the stable toolCallId/name
 * needed to update one Assistant UI tool part in place. Transcript contracts stay byte-for-byte unchanged.
 */
export type ChatActivityItem = Omit<ChatActivityRow, 'outcome' | 'sequence'> & {
  readonly toolCallId?: string;
  readonly toolName?: string;
  readonly outcome: ChatActivityRow['outcome'] | 'running';
  readonly sequence?: number;
};

/** The status of one plan step (BUILD-DEC-099) — mirrors the owning module's closed lifecycle. */
export type PlanStepStatus = 'pending' | 'in_progress' | 'done' | 'blocked';

/** One step of the agent's plan / to-do list — a sanitised description + its status. */
export interface PlanStep {
  readonly id: string;
  readonly text: string;
  readonly status: PlanStepStatus;
}

/** The CURRENT plan / to-do list the dock renders inline (BUILD-DEC-099) — the ChatGPT/Claude-style
 *  checklist the agent maintains and revises as it works. `text` is already client-surface-cleared. */
export interface ChatPlanView {
  readonly revision: number;
  readonly updatedAt: string;
  readonly steps: readonly PlanStep[];
}

/** The transcript view the dock renders for one run (GET /chat/transcript/:runId). It is EXACTLY the owning
 *  module's `getRunTranscript` projection reshaped for the wire — the dashboard adds nothing (N-CHAT-3 AC-1,
 *  as amended by BUILD-DEC-098: the trace rides the same owning projection). */
export interface ChatTranscriptView {
  readonly runId: string;
  /** The run's lifecycle state — drives the honest "running / interrupted / done" marker + interrupt control. */
  readonly state: ChatRunState;
  /** WHY an interrupted run stopped: a human pressing Stop, or the platform reconciling a run that was
   *  still in flight when the service restarted. Absent on every other state. The dock used to say "You
   *  stopped Ritona" for both, so a deploy mid-run blamed the reader for something they had not done. */
  readonly interruptedBy?: 'human' | 'restart' | 'inactivity';
  /** Agent-owned quiet interval projected only for inactivity pauses; the browser does not mirror policy. */
  readonly inactivityTimeoutSeconds?: number;
  /** The resolved turns, in order. Empty while a fresh run has produced no turn yet (honest, not fabricated). */
  readonly turns: readonly ChatTranscriptTurn[];
  /** The run's durable sanitised activity trace, in order (empty when nothing was recorded — honest). */
  readonly activity: readonly ChatActivityRow[];
  /** Durable publication chronology for the resolved assistant turn. Optional only for legacy snapshots;
   *  when present it is the rendering authority, ahead of the legacy activity-then-text fallback. */
  readonly parts?: readonly ChatTranscriptPart[];
  /** The CURRENT plan / to-do list of the run's Session (BUILD-DEC-099), or null when the agent has
   *  authored none. The live-updating checklist the dock renders inline. */
  readonly plan: ChatPlanView | null;
}

/** WHERE a history row came from (server-decided). `schedule` rows are a Schedule's own conversation,
 *  visible to every authenticated human so a routine can be inspected (owner ruling 2026-08-02). */
export type ChatSessionOrigin = 'private_chat' | 'schedule';

/** One row of the chat HISTORY list (GET /chat/sessions — IFC-AGENT-021, BUILD-DEC-098).
 *  Everything is server-derived from owned rows; the dashboard stores nothing (Constraint 4). */
export interface ChatSessionRow {
  readonly sessionId: string;
  /** Which class of conversation this is — the rail LABELS it rather than letting the two blur. */
  readonly origin: ChatSessionOrigin;
  /** A private chat's earliest human turn, never a generated title; a routine's Schedule NAME. Empty when
   *  the server could resolve neither — an honest blank, never an invented string. */
  readonly title: string;
  readonly state: ChatRunState;
  readonly latestRunId: string;
  readonly lastActivityAt: string;
  readonly runCount: number;
}

/** The full multi-run conversation (GET /chat/sessions/:sessionId — IFC-AGENT-022, BUILD-DEC-098):
 *  every admitted run's turns + activity in chronological order. Unknown/not-permitted reads are a
 *  uniform 404. */
export interface ChatSessionTranscriptView {
  readonly sessionId: string;
  /** WHICH class this conversation is, server-decided. `schedule` also tells the dock it owns NONE of
   *  these runs, so the per-run surfaces (the live-preview stream, GET /chat/transcript/:runId) will
   *  refuse them — the durable session read plus the idle re-read are the whole record for a routine. */
  readonly origin: ChatSessionOrigin;
  readonly latestRunId: string;
  readonly state: ChatRunState;
  readonly runs: readonly ChatTranscriptView[];
}

/** The outcome of a chat send (POST /chat/send) — the `agent.spawnAgentRun` SpawnOutcome, reshaped for the
 *  wire. A `ran` send yields the run whose transcript the dock then reads; a `refused` send is surfaced
 *  honestly (a cap/authority refusal — the dashboard never retries a Fence-crossing write, §8); a `queued`
 *  send is PRESERVED (a run already holds the Session), never silently dropped (N-CHAT-2 AC-3, G-160). */
export type ChatSendResult =
  | { readonly kind: 'started'; readonly runId: string }
  /** A refusal carries a sanitised `note` when the TRANSPORT built one (non-OK response) and the owning
   *  module's typed `reason` when the spawn itself refused (the wire SpawnOutcome carries `reason`, not
   *  `note` — deep-sweep 2026-07-09 F2: assuming `note` made spawn-time refusals render as undefined,
   *  i.e. nothing). The dock renders whichever is present, with an honest fallback. */
  | { readonly kind: 'refused'; readonly note?: string; readonly reason?: string }
  | { readonly kind: 'queued' };

/** Result of the run-id-only Continue intent. Every other continuation fact is server-derived. */
export type ChatContinueResult =
  | { readonly ok: true; readonly runId: string }
  | { readonly ok: false; readonly note: string };

/** The Today (home) aggregate projection — the home screen composed from the owning reads. */
// MIRRORED_CONTRACT: `TodayView` in dashboard/api/read-projections.ts.
export interface TodayProjection {
  /** Open Tasks: the non-terminal set (nothing forgotten). */
  readonly openTasks: readonly NonTerminalTaskRow[];
  /** The workload/cap headroom the Today stat panel shows (no hidden default). */
  readonly workload: {
    readonly openCount: number;
    readonly cap: number | null;
    readonly headroom: number | null;
  };
  /** The pending actor-scoped Gates — the same required items the Approvals inbox carries. */
  readonly gates: readonly ApprovalItem[];
  /** The KPIs to watch — the exact same one Metric wire shape as the KPIs endpoint. */
  readonly metrics: MetricsBoardWire['metrics'];
  /** Schedule health — the headline health rows (Today shows the summary; Scheduler shows detail). */
  readonly scheduleHealth: ScheduleHealthProjection;
  /** The "What Ritona did" activity rail — the sanitised Audit projection, newest first. */
  readonly activity: readonly ActivityRow[];
}
